/**
 * 将 Markdown（GFM，含表格）转为 docx 的 section children
 */
import {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  HeadingLevel,
  WidthType,
  TableLayoutType,
  BorderStyle
} from 'docx'
import marked from 'marked'

marked.setOptions({
  gfm: true,
  breaks: true
})

const FONT = 'Microsoft YaHei'
const FONT_MONO = 'Consolas'

const HEADINGS = [
  HeadingLevel.HEADING_1,
  HeadingLevel.HEADING_2,
  HeadingLevel.HEADING_3,
  HeadingLevel.HEADING_4,
  HeadingLevel.HEADING_5,
  HeadingLevel.HEADING_6
]

/** 引用块内段落/代码的左侧强调（与旧版 Vue 中紫色条一致） */
const BLOCKQUOTE_PROPS = {
  indent: { left: 720 },
  border: {
    left: {
      color: 'A855F7',
      space: 8,
      style: BorderStyle.SINGLE,
      size: 12
    }
  }
}

function emptyParagraph() {
  return new Paragraph({ children: [new TextRun({ text: ' ', font: FONT })] })
}

function inlineToRuns(tokens, base = {}) {
  if (!tokens || !tokens.length) {
    return [new TextRun({ text: ' ', font: FONT, ...base })]
  }
  const runs = []
  for (const t of tokens) {
    switch (t.type) {
      case 'text':
        runs.push(new TextRun({ text: t.text || '', font: FONT, ...base }))
        break
      case 'strong':
        runs.push(...inlineToRuns(t.tokens, { ...base, bold: true }))
        break
      case 'em':
      case 'emphasis':
        runs.push(...inlineToRuns(t.tokens, { ...base, italics: true }))
        break
      case 'codespan':
        runs.push(
          new TextRun({
            text: t.text || '',
            font: FONT_MONO,
            size: 20,
            ...base
          })
        )
        break
      case 'link':
        runs.push(...inlineToRuns(t.tokens, base))
        break
      case 'br':
        runs.push(new TextRun({ text: '\n', font: FONT, ...base }))
        break
      case 'del':
        runs.push(...inlineToRuns(t.tokens, { ...base, strike: true }))
        break
      case 'escape':
        runs.push(new TextRun({ text: t.text || '', font: FONT, ...base }))
        break
      default:
        if (t.tokens) runs.push(...inlineToRuns(t.tokens, base))
        else if (t.text) runs.push(new TextRun({ text: t.text, font: FONT, ...base }))
        break
    }
  }
  return runs.length ? runs : [new TextRun({ text: ' ', font: FONT })]
}

function cellParagraph(cell) {
  return new Paragraph({ children: inlineToRuns(cell.tokens) })
}

function tableTokenToTable(token) {
  const n = token.header.length
  const colWidth = n > 0 ? Math.floor(9000 / n) : 1000
  const headerCells = token.header.map(
    c =>
      new TableCell({
        shading: { fill: 'E8E8E8' },
        children: [cellParagraph(c)],
        margins: { top: 100, bottom: 100, left: 140, right: 140 }
      })
  )
  const headerRow = new TableRow({ tableHeader: true, children: headerCells })
  const bodyRows = token.rows.map(
    row =>
      new TableRow({
        children: row.map(
          c =>
            new TableCell({
              children: [cellParagraph(c)],
              margins: { top: 100, bottom: 100, left: 140, right: 140 }
            })
        )
      })
  )
  return new Table({
    rows: [headerRow, ...bodyRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: Array(n).fill(colWidth),
    layout: TableLayoutType.FIXED,
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
      left: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
      right: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'AAAAAA' },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'AAAAAA' }
    }
  })
}

function listMarker(token, itemNum, taskPrefix) {
  const t = taskPrefix || ''
  if (token.ordered) return `${t}${itemNum}. `
  return `${t}• `
}

/**
 * @param {import('marked').Token} token
 * @param {number} depth
 * @param {{ blockquote?: boolean }} ctx
 */
function listTokenToDocx(token, depth = 0, ctx = {}) {
  const out = []
  let itemNum = token.ordered ? Number(token.start) || 1 : 0
  const pad = '  '.repeat(depth)

  for (const item of token.items || []) {
    const taskPrefix = item.task ? (item.checked ? '☑ ' : '☐ ') : ''
    let firstInItem = true
    const blocks = (item.tokens || []).filter(t => t.type !== 'space')

    for (const bt of blocks) {
      if (bt.type === 'list') {
        if (firstInItem) {
          const m = listMarker(token, itemNum, taskPrefix)
          out.push(
            new Paragraph({
              ...(ctx.blockquote ? BLOCKQUOTE_PROPS : {}),
              children: [new TextRun({ text: pad + m, font: FONT })]
            })
          )
          firstInItem = false
        }
        out.push(...listTokenToDocx(bt, depth + 1, ctx))
        continue
      }

      const marker = firstInItem ? listMarker(token, itemNum, taskPrefix) : ''
      firstInItem = false

      if (bt.type === 'paragraph') {
        const runs = [new TextRun({ text: pad + marker, font: FONT })]
        runs.push(...inlineToRuns(bt.tokens))
        out.push(
          new Paragraph({
            ...(ctx.blockquote ? BLOCKQUOTE_PROPS : {}),
            children: runs
          })
        )
        continue
      }

      if (bt.type === 'code') {
        const runs = [new TextRun({ text: pad + marker, font: FONT })]
        runs.push(
          new TextRun({
            text: bt.text || ' ',
            font: FONT_MONO,
            size: 20
          })
        )
        out.push(
          new Paragraph({
            ...(ctx.blockquote ? BLOCKQUOTE_PROPS : {}),
            ...(ctx.blockquote ? {} : { shading: { fill: 'F2F2F2' } }),
            children: runs
          })
        )
        continue
      }

      const inner = blockTokenToDocx(bt, ctx)
      if (marker) {
        out.push(
          new Paragraph({
            ...(ctx.blockquote ? BLOCKQUOTE_PROPS : {}),
            children: [new TextRun({ text: pad + marker, font: FONT })]
          })
        )
      }
      out.push(...inner)
    }

    if (token.ordered) itemNum += 1
  }

  return out
}

/**
 * @param {import('marked').Token} token
 * @param {{ blockquote?: boolean }} ctx
 * @returns {import('docx').FileChild[]}
 */
function blockTokenToDocx(token, ctx = {}) {
  const bq = ctx.blockquote ? BLOCKQUOTE_PROPS : {}

  switch (token.type) {
    case 'space':
      return []
    case 'heading': {
      const level = Math.min(Math.max(token.depth || 1, 1), 6) - 1
      return [
        new Paragraph({
          ...bq,
          heading: HEADINGS[level],
          children: inlineToRuns(token.tokens)
        })
      ]
    }
    case 'paragraph':
      return [new Paragraph({ ...bq, children: inlineToRuns(token.tokens) })]
    case 'table':
      return [tableTokenToTable(token), emptyParagraph()]
    case 'code':
      return [
        new Paragraph({
          ...bq,
          ...(!ctx.blockquote ? { shading: { fill: 'F2F2F2' } } : {}),
          children: [
            new TextRun({
              text: token.text || ' ',
              font: FONT_MONO,
              size: 20
            })
          ]
        })
      ]
    case 'blockquote':
      return tokensToDocxChildren(token.tokens || [], { blockquote: true })
    case 'list':
      return listTokenToDocx(token, 0, ctx)
    case 'hr':
      return [
        new Paragraph({
          border: {
            bottom: { color: 'CCCCCC', space: 1, style: BorderStyle.SINGLE, size: 6 }
          },
          children: [new TextRun({ text: ' ', font: FONT })]
        })
      ]
    default:
      if (token.raw) {
        return [
          new Paragraph({
            ...bq,
            children: [new TextRun({ text: token.raw.replace(/\n+$/, ''), font: FONT })]
          })
        ]
      }
      return []
  }
}

function tokensToDocxChildren(tokens, ctx = {}) {
  const children = []
  for (const token of tokens || []) {
    children.push(...blockTokenToDocx(token, ctx))
  }
  return children
}

/**
 * @param {string} markdown
 * @returns {import('docx').FileChild[]}
 */
export function markdownToDocxChildren(markdown) {
  const src = markdown == null ? '' : String(markdown)
  if (!src.trim()) {
    return [emptyParagraph()]
  }

  const tokens = marked.lexer(src)
  const children = tokensToDocxChildren(tokens, {})

  return children.length ? children : [emptyParagraph()]
}
